'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '~/src/lib/utils'
import { SeoReportProductCard } from '~/src/components/reports/SeoReportProductCard'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BuyLink {
  label: string
  url: string
  price?: number | null
  logoUrl?: string | null
  verified?: boolean
}

interface SeoTopPickCardProps {
  slug: string
  roleLabel: string
  roleDescription: string
  name: string
  brand: string | null
  image: string | null
  summary: string | null
  buyLinks: BuyLink[]
}

interface SeoReportTopPicksProps {
  picks: SeoTopPickCardProps[]
  description?: string | null
  isImageChecking?: boolean
  onViewProduct?: (slug: string) => void
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopPickCard({
  pick,
  index,
  isImageChecking,
  onViewProduct,
}: {
  pick: SeoTopPickCardProps
  index: number
  isImageChecking?: boolean
  onViewProduct?: (slug: string) => void
}) {
  const isFirst = index === 0
  const isClickable = Boolean(onViewProduct)
  // Card-level guard: nested <a> or <button> handles its own click.
  const handleArticleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!onViewProduct) return
    if ((e.target as HTMLElement).closest('a, button')) return
    onViewProduct(pick.slug)
  }
  const handleArticleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!isClickable) return
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (onViewProduct) onViewProduct(pick.slug)
    }
  }

  return (
    <article
      className={cn(
        'top-pick-card flex w-[82vw] flex-none snap-start self-stretch flex-col rounded-3xl border bg-white p-4 md:w-[320px]',
        isFirst && 'border border-ds-border-brand-alt bg-white/95',
        !isFirst &&
          'border-[0.5px] border-border shadow-[0px_4px_6px_0px_rgba(10,13,18,0.05),0px_2px_4px_0px_rgba(10,13,18,0.04)]' /* color-token-exception: multi-layer box-shadow using ink-equivalent opacity */,
        isClickable && 'cursor-pointer'
      )}
      // As a role=button the card names itself from visible content — an
      // override label that doesn't contain that text fails WCAG 2.5.3
      // (label-content-name-mismatch). Keep the label on the inert article.
      aria-label={isClickable ? undefined : `${pick.roleLabel}: ${pick.name}`}
      onClick={isClickable ? handleArticleClick : undefined}
      onKeyDown={isClickable ? handleArticleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      style={
        isFirst
          ? {
              boxShadow:
                '0 0 2px #EBFFFF, 0 0 4px #EBFFFF, 0 0 15px #EBFFFF, 0 0 29px #EBFFFF, 0 0 50px #EBFFFF, 0 0 87px #EBFFFF, 0px 6px 12px -2px rgba(10,13,18,0.12), 0px 3px 6px -2px rgba(10,13,18,0.08)',
            }
          : undefined
      }
    >
      <SeoReportProductCard
        images={pick.image ? [pick.image] : []}
        name={pick.name}
        roleLabel={pick.roleLabel}
        roleDescription={pick.roleDescription}
        summary={pick.summary}
        buyLinks={pick.buyLinks}
        slug={pick.slug}
        variant='compact'
        index={index}
        isImageChecking={isImageChecking}
        onViewProduct={onViewProduct}
        analyticsSection='top-picks'
        analyticsPosition={index}
      />
    </article>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SeoReportTopPicks({
  picks,
  description,
  isImageChecking = false,
  onViewProduct,
}: SeoReportTopPicksProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activePage, setActivePage] = useState(0)

  const cardWidth = 320
  const cardGap = 16

  const scrollToPage = useCallback(
    (page: number) => {
      if (!scrollRef.current) return
      const container = scrollRef.current
      // Calculate how many cards are visible
      const containerWidth = container.clientWidth
      const cardsPerPage = Math.max(
        1,
        Math.floor((containerWidth + cardGap) / (cardWidth + cardGap))
      )
      const totalPgs = Math.ceil(picks.length / cardsPerPage)
      const clampedPage = Math.max(0, Math.min(page, totalPgs - 1))
      const scrollTarget = clampedPage * cardsPerPage * (cardWidth + cardGap)
      container.scrollTo({ left: scrollTarget, behavior: 'smooth' })
      setActivePage(clampedPage)
    },
    [picks.length]
  )

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const container = scrollRef.current
    const containerWidth = container.clientWidth
    const cardsPerPage = Math.max(
      1,
      Math.floor((containerWidth + cardGap) / (cardWidth + cardGap))
    )
    const totalPgs = Math.ceil(picks.length / cardsPerPage)
    const scrollLeft = container.scrollLeft
    const maxScroll = container.scrollWidth - containerWidth
    const page =
      maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * (totalPgs - 1)) : 0
    setActivePage(page)
  }, [picks.length])

  const computeTotalPages = useCallback(() => {
    if (!scrollRef.current) return 1
    const containerWidth = scrollRef.current.clientWidth
    const cardsPerPage = Math.max(
      1,
      Math.floor((containerWidth + cardGap) / (cardWidth + cardGap))
    )
    return Math.ceil(picks.length / cardsPerPage)
  }, [picks.length])

  // Check initial scroll state on mount so the right fade doesn't linger
  // when all cards already fit in the viewport
  useEffect(() => {
    handleScroll()
  }, [handleScroll])

  if (picks.length === 0) return null

  return (
    <section className='flex flex-col gap-6'>
      <div className='relative z-30 flex flex-col gap-2'>
        <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
          All of our top picks
        </h2>
        {description && (
          <p className='text-base leading-6 text-foreground-secondary'>
            {description}
          </p>
        )}
      </div>

      <div className='flex flex-col gap-6'>
        {/* Scrollable card track with fade edge */}
        <div className='relative'>
          {/* Desktop covers for edge fading */}
          <div
            className='pointer-events-none absolute -left-[140px] -top-[100px] -bottom-[50px] z-20 hidden w-[140px] bg-gradient-to-r from-white from-[72%] to-transparent md:block'
            aria-hidden='true'
          />
          <div
            className='pointer-events-none absolute -right-[120px] -top-[100px] -bottom-[10px] z-20 hidden w-[140px] bg-gradient-to-l from-white from-[72%] to-transparent md:block'
            aria-hidden='true'
          />

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className='-mx-4 -my-[60px] scrollbar-none flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 py-[60px] [scroll-padding-inline:16px] touch-pan-x touch-pan-y md:-mx-[100px] md:-my-[100px] md:px-[100px] md:py-[100px] md:[scroll-padding-inline:100px]'
            role='region'
            aria-label='Top picks carousel'
          >
            {picks.map((pick, index) => (
              <TopPickCard
                key={pick.slug}
                pick={pick}
                index={index}
                isImageChecking={isImageChecking}
                onViewProduct={onViewProduct}
              />
            ))}
          </div>
        </div>

        {/* Pagination + arrows */}
        <div className='relative z-30 flex items-center justify-center md:pl-[72px]'>
          {/* Dots */}
          <div className='flex flex-1 items-center justify-center gap-4'>
            {Array.from({ length: computeTotalPages() }).map((_, i) => (
              <button
                key={`dot-${i}`}
                type='button'
                onClick={() => scrollToPage(i)}
                className={cn(
                  'h-2.5 w-2.5 rounded-full transition-colors',
                  activePage === i ? 'bg-foreground' : 'bg-border'
                )}
                aria-label={`Go to page ${i + 1}`}
                aria-current={activePage === i ? 'true' : undefined}
              />
            ))}
          </div>

          {/* Arrow buttons */}
          <div className='hidden gap-2 md:flex'>
            <button
              type='button'
              onClick={() => scrollToPage(activePage - 1)}
              disabled={activePage === 0}
              className='flex h-8 w-8 items-center justify-center rounded-full border border-border bg-white transition-colors hover:bg-gray-50 disabled:opacity-40'
              aria-label='Previous page'
            >
              <ChevronLeft className='h-5 w-5 text-foreground' />
            </button>
            <button
              type='button'
              onClick={() => scrollToPage(activePage + 1)}
              disabled={activePage >= computeTotalPages() - 1}
              className='flex h-8 w-8 items-center justify-center rounded-full border border-border bg-white transition-colors hover:bg-gray-50 disabled:opacity-40'
              aria-label='Next page'
            >
              <ChevronRight className='h-5 w-5 text-foreground' />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
